import './DatasourceExplorer.scss';
import { Flex, Segmented, Tooltip, Select } from 'antd';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Col, Form, Row } from 'react-bootstrap';
import AppSearch from '../common/AppSearch';
import { BdaDataset } from '../../models/BdaDataset';
import { BdaModel } from '../../models/BdaModel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Datasource, ObjectStorageDatasource } from '../../models/Datasource';
import { useToasts } from '../../contexts/toasts';
import { fixDirName, normalizePath, setObjectType } from '../../utils';
import { QueryDetails, VizClient } from '../../api/VizClient';
import { DatasourceListItem } from '../forms/AssetEditForm';
import AlidaLoader from '../common/AlidaLoader';
import { Datasources } from '../../api/Datasources';
import UploadModal from './UploadModal';
import IconButton from '../common/IconButton';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';
import ScriptEditor from '../common/ScriptEditor';
import PathExplorer from './PathExplorer';
import classNames from 'classnames';

const typeOptions = [
  { value: "table", icon: <Tooltip title="Table" mouseEnterDelay={0.5}><FontAwesomeIcon icon={solid('table')} /></Tooltip> },
  { value: "generic", icon: <Tooltip title="Generic" mouseEnterDelay={0.5}><FontAwesomeIcon icon={solid('photo-film')} /></Tooltip> }
];

type Props = {
  asset?: Partial<BdaDataset | BdaModel>;
  type: string;
  onTypeChange: React.Dispatch<React.SetStateAction<string>>;
  mode: 'dataset' | 'model';
  validated?: boolean;
  invalidAsset?: boolean;
  table: any;
  onTableChange: React.Dispatch<React.SetStateAction<any>>;
  datasource?: Datasource;
  onDatasourceChange: (datasource: Datasource) => void;
  onPathChange: (path: string, containsDataset?: boolean) => void;
  onDetailsChange?: (value: string | undefined) => void;
  selectedPath?: string;
}

function DatasourceExplorer({
  asset, mode, validated, onDetailsChange,
  type, onTypeChange, table, onTableChange,
  datasource, onDatasourceChange, onPathChange, invalidAsset,
  selectedPath
}: Props) {
  const datasourceContainer = useRef<HTMLDivElement | null>(null);
  const [datasourceSearch, setDatasourceSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [datasources, setDatasources] = useState<Datasource[]>();
  const [path, setPath] = useState<string>();
  const [tables, setTables] = useState<any[]>();
  const [error, setError] = useState(false);
  const [containsDataset, setContainsDataset] = useState<boolean>();

  const { showToast } = useToasts();

  const pathParts = useMemo(() => (path || "").split("/"), [path]);
  const showSelectPathButton = useMemo(() => datasource && pathParts.length > 1 && ((!(asset as BdaDataset).datasetFileType?.path && !(asset as BdaDataset).datasetFileType?.tableName) || (((asset as BdaDataset).datasetFileType?.path && path !== (asset as BdaDataset).datasetFileType?.path) || ((asset as BdaDataset).datasetFileType?.tableName && path !== (asset as BdaDataset).datasetFileType?.tableName))), [datasource, asset, path, pathParts]);
  const datasourceLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "datasources"));

  const initDatasources = useCallback(() => {
    Datasources.getDatasources()
      .then(
        ({ data }) => setDatasources(data),
        (err) => {
          setDatasources([]);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('datasources-error', message, `Datasources Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showToast]);

  useEffect(initDatasources, [initDatasources]);

  useEffect(() => {
    if (path === undefined && asset?.id) {
      // const path = ((asset as BdaDataset).datasetFileType!.tableName || (asset as BdaDataset).datasetFileType!.path) + "/";
      let tmpPath = "";
      if (mode === "model" && setObjectType<BdaModel>(asset) && setObjectType<ObjectStorageDatasource>(asset.datasource))
        tmpPath = (asset.datasource.prefixPath ? asset.datasource.prefixPath + "/" : "") + asset.location;
      else if (setObjectType<BdaDataset>(asset) && setObjectType<ObjectStorageDatasource>(asset.datasource))
        tmpPath = (asset.datasource.prefixPath ? asset.datasource.prefixPath + "/" : "") + (asset.datasetFileType!.tableName || asset.datasetFileType!.path) + "/";
      setPath(normalizePath(tmpPath!));
    }
  }, [asset, path, mode]);

  useEffect(() => {
    if (asset?.id && datasources && datasource) {
      const selectedDatasource = document.querySelector(".explorer-sidebar div.datasource-list-item.selected") as HTMLButtonElement;
      if (selectedDatasource) datasourceContainer.current!.scrollTop = selectedDatasource!.offsetTop - datasourceContainer.current!.offsetTop;
      if (selectedDatasource) datasourceContainer.current!.scrollTo({ top: selectedDatasource!.offsetTop - datasourceContainer.current!.offsetTop });
    }
  }, [asset, datasources, datasource]);

  const getTables = useCallback(() => {
    const body = new QueryDetails(datasource);
    const isMessageBroker = datasource!.type === 'messageBroker';

    const promise = isMessageBroker
      ? VizClient.getTopics(body)
      : VizClient.getTables(body);

    promise.then(
      ({ data }) => setTables(data || []),
      (err) => {
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('tables-error', message, `${isMessageBroker ? 'Topics' : 'Tables'} Error ${status ? `(${status})` : ''}`, 'danger');
        setError(true);
      },
    )
  }, [datasource, showToast]);

  useEffect(() => {
    if (datasource && datasource.type === "remote") getTables();
  }, [datasource, getTables]);

  const openUploadModal = useCallback(() => setShowModal(true), []);

  const handleSelectPath = useCallback(() => {
    onPathChange(path || "", containsDataset);
  }, [onPathChange, path, containsDataset]);

  const handleRefetchPath = useCallback((p: string, containsDataset: boolean) => {
    if (selectedPath) onPathChange(p, containsDataset);
  }, [onPathChange, selectedPath]);

  const handleSelectDatasource = useCallback((datasource: Datasource) => () => {
    setError(false);
    setTables(undefined);
    onTableChange(null);
    // TODO is this needed?
    // setPath(datasource.prefixPath ? [fixDirName(datasource.prefixPath)] : ['']);
    onDatasourceChange(datasource);
    if (datasource.type === 'objectStorage' && setObjectType<ObjectStorageDatasource>(datasource)) setPath(datasource.prefixPath ? fixDirName(datasource.prefixPath) : "");
    if (datasource.type === 'remote') onTypeChange('endpoint');
  }, [onDatasourceChange, onTableChange, onTypeChange]);

  const handleSelectTable = useCallback((tableName: string) => {
    const table = tables!.find(table => table.name === tableName);
    onTableChange(table);
  }, [tables, onTableChange]);

  const handleModalClose = useCallback(() => setShowModal(false), []);

  const renderDatasourceContent = useCallback(() => {
    if (datasource!.type === "remote") return (
      <div className="m-2">
        <Form.Group controlId="dataset-details">
          <Form.Label>Details</Form.Label>
          <ScriptEditor value={(asset as BdaDataset).datasetFileType?.details} onChange={onDetailsChange} height={250} />
        </Form.Group>
      </div>
    )

    if (datasource!.type !== "objectStorage") return (
      <div className="m-2">
        <Form.Label>{datasource?.type === 'messageBroker' ? 'Topic' : 'Table'}</Form.Label>
        <Select
          showSearch
          allowClear
          loading={!tables}
          className="w-100"
          placeholder={`Choose ${datasource?.type === 'messageBroker' ? 'Topic' : 'Table'}`}
          status={(validated && !table) ? 'error' : undefined}
          optionFilterProp="label"
          onChange={handleSelectTable}
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          options={tables?.map(table => ({
            label: table.name,
            value: table.name,
          }))}
          value={table?.name}
        />
      </div>
    )

    return (
      <PathExplorer
        actions={mode === 'dataset' && (
          <Segmented options={typeOptions} value={type} onChange={onTypeChange} />
        )}
        pathActions={
          <>
            {showSelectPathButton && (
              <IconButton type="button" tooltip="Select Path" onClick={handleSelectPath} className={classNames("p-1 me-1", { blinking: validated && invalidAsset })}>
                <FontAwesomeIcon icon={regular('check-circle')} size="sm" />
                {validated && invalidAsset && <span className="ms-2">Select Path</span>}
              </IconButton>
            )}
            <IconButton type="button" tooltip="Upload files" onClick={openUploadModal} className="p-1">
              <FontAwesomeIcon icon={solid('upload')} size="sm" />
            </IconButton>
          </>
        }
        datasource={datasource}
        path={path}
        onPathChange={setPath}
        onCsvPathVisited={setContainsDataset}
        onRefetchPath={handleRefetchPath}
      />
    )
  }, [handleSelectTable, table, datasource, tables, validated, asset, onDetailsChange, mode, onTypeChange, openUploadModal, path, type, handleSelectPath, showSelectPathButton, invalidAsset, handleRefetchPath]);

  const renderAsyncContent = useCallback(() => {
    if (!datasource) return (
      <Flex align="center" justify="center" className="bg-light h-100 text-muted">
        <FontAwesomeIcon icon={solid('faucet-drip')} size="2x" />
        <h5 className="mb-0 ms-3">Choose one {datasourceLabels.singular} to see details here.</h5>
      </Flex>
    );

    if (error) return (
      <Alert variant="danger" className="py-2 m-2">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occurred with the selected data source. Try again later</span>
      </Alert>
    );

    return renderDatasourceContent();
  }, [datasource, renderDatasourceContent, error, datasourceLabels]);

  return (
    <>
      <Row className="datasource-explorer gx-0 mx-0">
        <Col md={3}>
          <aside className="explorer-sidebar border-end" ref={datasourceContainer}>
            <div className="p-1 px-2 border-bottom">
              <AppSearch placeholder="Search datasource..." value={datasourceSearch} onChange={setDatasourceSearch} />
            </div>
            <div className="sidebar-content">
              {datasources
                ? datasources
                  .filter(datasource => datasource.name.toLowerCase().includes(datasourceSearch.toLowerCase()))
                  .map(d => (
                    <DatasourceListItem key={d.id} datasource={d} onClick={handleSelectDatasource(d)} selected={datasource?.id === d.id} />
                  ))
                : (
                  <div className="d-flex py-5 justify-content-center">
                    <AlidaLoader size={100} />
                  </div>
                )
              }
            </div>
          </aside>
        </Col>
        <Col>
          <div className="h-100">{renderAsyncContent()}</div>
        </Col>
      </Row>
      {validated && invalidAsset && (
        <Form.Text>
          <FontAwesomeIcon icon={regular('question-circle')} />
          <span className="ms-1">
            To select a path you must navigate one of the directories on the right and then click on the check icon next to the current location.
            {' '}<b>The selected path cannot be the root folder.</b>
          </span>
        </Form.Text>
      )}

      <UploadModal show={showModal} onClose={handleModalClose} selectedDatasource={datasource} selectedPath={normalizePath(path)} />
    </>
  )
}

export default memo(DatasourceExplorer);